import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { IFilter, IOfferingSearch } from 'utils/globalTypes';
import { thumbnailImage } from 'utils/images';
import { safeRunRequest } from 'utils/networkRequests';
import { getObjProperty } from 'utils/object';
import { getSingleValue } from 'utils/urlUtils';

import { ResultMapper } from './interfaces';

class SearchStore {
  searchItems: {
    suggestions?: IOfferingSearch[];
    locations?: IOfferingSearch[];
  } = {};

  currentSearchTerm = '';
  isSearching = false;

  suggestionsQuery = {
    resultsPerPage: 5
  };

  constructor() {
    makeObservable(this, {
      searchItems: observable,
      isSearching: observable,
      currentSearchTerm: observable,

      setCurrentSearchTerm: action,
      setCurrentSearch: action,
      setIsSearching: action,
      clearSearch: action,

      searchResultsLength: computed,
      getSearchItems: computed,
      getIsSearching: computed,
      getCurrentSearchTerm: computed
    });
  }

  searchFilter(array: ResultMapper[], field: string) {
    return array.filter((item) => {
      if (!item?.highlight) {
        return getObjProperty(item, field);
      }
      return getObjProperty(item?.highlight, field);
    });
  }

  resultsMapper(requestData: ResultMapper[], highlight: string) {
    return (
      requestData &&
      this.searchFilter(requestData, highlight).map(
        (item) =>
          ({
            id: item.id,
            offering_id: item.offering_id,
            offering_name: item?.highlight?.offering_name || item.offering_name,
            asset_type: item.asset_type,
            gallery_media: [
              { original: thumbnailImage(getSingleValue(item.media)), title: item.offering_name }
            ],
            location: item?.highlight?.formatted_place || item.formatted_place,
            formatted_place: item.formatted_place,
            quantity_holding_investor: item.quantity_holding_investor,
            quantity_executed_int_buy_investor: item.quantity_executed_int_buy_investor,
            quantity_executed_bid_buy_investor: item.quantity_executed_bid_buy_investor,
            quantity_executed_bid_sell_investor: item.quantity_executed_bid_sell_investor,
            quantity_pending_int_buy_investor: item.quantity_pending_int_buy_investor,
            quantity_pending_bid_buy_investor: item.quantity_pending_bid_buy_investor,
            quantity_pending_bid_sell_investor: item.quantity_pending_bid_sell_investor
          } as IOfferingSearch)
      )
    );
  }

  async setCurrentSearchTerm(searchTerm?: string) {
    this.currentSearchTerm = searchTerm || '';
  }

  async setCurrentSearch(searchTerm: string, filters?: IFilter[], requestCmd?: string) {
    this.currentSearchTerm = searchTerm;

    if (!searchTerm) {
      this.clearSearch();
    }

    const query = { searchTerm: searchTerm, filters: filters, ...this.suggestionsQuery };
    if (query?.searchTerm) {
      const queryBody = {
        offering_filter: searchTerm
      };

      return safeRunRequest(requestCmd || 'searchOfferings', queryBody).then((json) => {
        // search value changed. ignore old results
        if (query?.searchTerm != this.currentSearchTerm) {
          return;
        }

        const { data: queryItems } = json;
        const uniqueItems = queryItems?.filter(
          (item: ResultMapper, index: number, self: ResultMapper[]) =>
            index === self.findIndex((other) => other.offering_id === item.offering_id)
        );

        runInAction(() => {
          this.searchItems = {
            locations: this.resultsMapper(uniqueItems, 'formatted_place'),
            suggestions: this.resultsMapper(uniqueItems, 'offering_name')
          };
        });

        if (searchTerm) {
          const { locations, suggestions } = this.searchItems;
          this.setIsSearching(!(locations?.length && suggestions?.length));
        }
      });
    }
  }

  setIsSearching(value: boolean) {
    this.isSearching = value;
  }

  clearSearch() {
    this.setIsSearching(false);
    this.currentSearchTerm = '';
    this.searchItems = {};
  }

  get getSearchItems() {
    return this.searchItems;
  }

  get searchResultsLength() {
    const { locations, suggestions } = this.getSearchItems;

    return locations?.length === 0 && suggestions?.length === 0;
  }

  get getCurrentSearchTerm() {
    return this.currentSearchTerm;
  }

  get getIsSearching() {
    return this.isSearching;
  }
}

export default SearchStore;
